import { Icon } from '@lego/connect-components-react';
import { Flex, HorizontalStack, Tag, Text } from '@lego/klik-ui';
import { CheckTickBold, CircleXmarkSolid } from '@lego/klik-ui/icons';
import type {
  ClearIndicatorProps,
  GroupBase,
  MultiValueProps,
  OptionProps,
} from 'chakra-react-select';
import { chakraComponents } from 'chakra-react-select';
import type { ReactElement } from 'react';
import type { DefaultOptionType } from './combobox';

// Custom option component with a checkmark icon for selected options
export const Option = <T extends DefaultOptionType, IsMulti extends boolean>({
  ...props
}: OptionProps<T, IsMulti, GroupBase<T>>): ReactElement => {
  return (
    <chakraComponents.Option {...props}>
      <Flex align="center" justifyContent="space-between" width="100%">
        <HorizontalStack gap="8px">
          <Text>{props.data.label}</Text>
        </HorizontalStack>
        {props.isSelected && (
          <CheckTickBold color="slate.900" height="16px" width="16px" />
        )}
      </Flex>
    </chakraComponents.Option>
  );
};

// Custom multi value component with a Klik tag component
export const MultiValue = <
  T extends DefaultOptionType,
  IsMulti extends boolean,
>({
  children,
  data,
  removeProps,
}: MultiValueProps<T, IsMulti>): ReactElement => {
  const showError = 'hasError' in data && data.hasError;
  const colorScheme = showError ? 'magenta' : undefined;
  const hasIcon = Boolean(data.iconName);

  // Using a div to wrap the close button to reconcile type differences between HTMLDivElement and HTMLButtonElement
  return (
    <Tag colorScheme={colorScheme} height="32px" maxWidth="100%">
      <Tag.Label
        borderStartRadius="8px !important"
        sx={{
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {showError ? (
          <Icon
            aria-label="Changing Color Icon"
            color="ds-color-content-negative"
            name="status-circle-exclamation-solid"
            size="small"
          />
        ) : hasIcon && data.iconName ? (
          <Icon
            aria-label={`Icon for ${data.iconName}`}
            name={data.iconName}
            size="small"
          />
        ) : null}
        {children}
      </Tag.Label>
      <Flex
        {...removeProps}
        overflow="hidden"
        onClick={removeProps.onClick}
        borderEndRadius="8px !important"
        justifyContent="center"
      >
        <Tag.CloseButton />
      </Flex>
    </Tag>
  );
};

// Custom clear indicator component with a Klik cross icon
export const ClearIndicator = <T, IsMulti extends boolean>(
  props: ClearIndicatorProps<T, IsMulti, GroupBase<T>>,
): ReactElement => {
  return (
    <chakraComponents.ClearIndicator {...props}>
      <CircleXmarkSolid
        color="#717384"
        height="20px"
        width="20px"
        marginRight="8px"
      />
    </chakraComponents.ClearIndicator>
  );
};
